import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ProviderType } from 'src/modules/user/models/user';
import defaultValuePropIcon from 'src/public/img/icon-clipboard.svg';
import valueIconDart from 'src/public/img/icon-dart.svg';
import valueIconMicroscope from 'src/public/img/icon-microscope.svg';
import valueIconPuzzle from 'src/public/img/icon-puzzle.svg';
import valueIconTalk from 'src/public/img/icon-talk.svg';
import valueIconTrophy from 'src/public/img/icon-trophy.svg';
import valueIconXray from 'src/public/img/icon-xray.svg';
import quotationMarksIcon from 'src/public/img/quotation-marks.svg';
import therapistImg from 'src/public/img/welcome-user.svg';
import coachImg from 'src/public/img/work-with-a-coach.svg';
import { ButtonGroup, Button } from '@modernhealth/vital-components';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

const TileWrapper = styled.div({
    padding: 20,
    marginLeft: 15,
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '32.5%',
    border: `2px solid ${color.linkWaterLight}`,
    borderRadius: 12,
    alignItems: 'center',
    [mediaQueries.sm]: {
        marginTop: 15,
        width: '90%',
    },
});

const QuotationMarksIcon = styled.img({
    position: 'absolute',
    top: 0,
    left: 0,
});

const TileTitle = styled.h4(
    {
        textAlign: 'center',
        color: color.fiord,
    },
    font.heavy(font.size.medium),
);

const TileIconWrapper = styled.div({
    maxWidth: 60,
    borderRadius: 38,
    backgroundColor: color.aquaSpring,
    marginBottom: 10,
    padding: 16,
});

const TileIcon = styled.img({
    width: 22,
});

interface TileDescriptionProps {
    quote?: boolean;
}

const TileDescription = styled.p<TileDescriptionProps>(
    props => ({
        textAlign: 'center',
        color: color.blueBayoux,
        fontStyle: props.quote ? 'oblique' : 'normal',
        minHeight: 40,
    }),
    font.book(font.size.medium),
);

interface TileProps {
    description: string;
    title?: string;
    icon?: string;
}

const Tile: React.FC<TileProps> = ({ description, title, icon }) => (
    <TileWrapper>
        {!title && <QuotationMarksIcon src={quotationMarksIcon} />}

        <TileIconWrapper>
            {icon && <TileIcon src={icon} />}
            {!icon && <TileIcon src={defaultValuePropIcon} />}
        </TileIconWrapper>

        {title && <TileTitle>{title}</TileTitle>}
        <TileDescription quote={!title}>{description}</TileDescription>
    </TileWrapper>
);

const coachData = [
    {
        icon: valueIconTrophy,
        title: 'coach.work_with.data.techniques.title',
        description: 'coach.work_with.data.techniques.description',
    },
    {
        icon: valueIconDart,
        title: 'coach.work_with.data.steps.title',
        description: 'coach.work_with.data.steps.description',
    },
    {
        icon: valueIconTalk,
        title: 'coach.work_with.data.focus.title',
        description: 'coach.work_with.data.focus.description',
    },
];

const therapistData = [
    {
        icon: valueIconMicroscope,
        title: 'therapist.work_with.data.techniques.title',
        description: 'therapist.work_with.data.techniques.description',
    },
    {
        icon: valueIconPuzzle,
        title: 'therapist.work_with.data.issues.title',
        description: 'therapist.work_with.data.issues.description',
    },
    {
        icon: valueIconXray,
        title: 'therapist.work_with.data.clinical_issues.title',
        description: 'therapist.work_with.data.clinical_issues.description',
    },
];

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '90%',
    maxHeight: 1000,
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'center',
    [mediaQueries.sm]: {
        marginTop: 50,
    },
});

const Title = styled.h3(
    {
        color: color.pickledBluewood,
        marginTop: 32,
        marginBottom: 20,
    },
    font.black(font.size.extraLarge),
);

const Description = styled.p({
    color: color.steelGray,
    maxWidth: 620,
    marginBottom: 30,
    textAlign: 'center',
    ...font.book(font.size.mediumExtended),
});

const TilesWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    maxWidth: 1050,
    [mediaQueries.sm]: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
});

const ButtonsWrapper = styled.div({
    marginTop: 40,
    width: '50%',
});

interface Props {
    providerType: ProviderType;
    description?: string;
    title?: string;
    buttonText?: string;
    onChooseProviderClick: () => void;
    onNotNowClick: () => void;
}

export const WorkWithProviderIntro: React.FC<Props> = ({
    providerType,
    onChooseProviderClick,
    onNotNowClick,
    description,
    title,
    buttonText,
}) => {
    const { t } = useTranslation();
    const isTherapist = providerType === ProviderType.THERAPIST;
    const data = isTherapist ? therapistData : coachData;
    const mainImg = isTherapist ? therapistImg : coachImg;
    const altText = isTherapist
        ? 'man with question mark speech bubble'
        : 'two women under umbrella';
    const titleToDisplay =
        title || t(`providers:intro.work_with.title.${providerType.toLowerCase()}`);

    return (
        <Wrapper>
            <img src={mainImg} alt={altText} />
            <Title>{titleToDisplay}</Title>
            {description ? <Description>{description}</Description> : null}

            <TilesWrapper>
                {data.map(tileData => (
                    <Tile
                        description={t(`providers:${tileData.description}`)}
                        title={t(`providers:${tileData.title}`)}
                        icon={tileData.icon}
                        key={tileData.description}
                    />
                ))}
            </TilesWrapper>
            <ButtonsWrapper>
                <ButtonGroup>
                    <Button variant="secondary" onClick={onNotNowClick} size="large">
                        {t('common.not_now')}
                    </Button>
                    <Button variant="primary" onClick={onChooseProviderClick} size="large">
                        {buttonText ||
                            (!isTherapist
                                ? t('providers:intro.choose_coach')
                                : t('common.continue'))}
                    </Button>
                </ButtonGroup>
            </ButtonsWrapper>
        </Wrapper>
    );
};
